import { useState, useCallback } from "react";
import Navbar from "@/components/feature/Navbar";
import AuthModal from "@/components/feature/AuthModal";
import Confetti from "@/components/base/Confetti";
import WorldMap, { SelectedBlock } from "./components/WorldMap";
import BlockDetailModal from "./components/BlockDetailModal";
import BuildModal from "./components/BuildModal";
import AuctionSetupModal from "./components/AuctionSetupModal";
import { MapSearchBar } from "./components/MapSearch";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import type { LatLng } from "@/contexts/GameContext";
import { calcAreaKm2 } from "./components/PolygonDrawer";
import { calcLandPrice, getLandValueByLatLng, getLandValueDescription } from "@/mocks/landValues";

// ─── アニメーション付きボタン ─────────────────────────────────
interface AnimBtnProps {
  onClick?: () => void;
  className?: string;
  children: React.ReactNode;
  animType?: "pop" | "bounce" | "shake";
  disabled?: boolean;
}

function AnimBtn({ onClick, className = "", children, animType = "pop", disabled }: AnimBtnProps) {
  const [anim, setAnim] = useState(false);
  const [showCoin, setShowCoin] = useState(false);

  const handleClick = useCallback(() => {
    if (disabled) return;
    setAnim(true);
    // コイン消費ボタンはコイン飛び出しエフェクト
    if (animType === "bounce") {
      setShowCoin(true);
      setTimeout(() => setShowCoin(false), 700);
    }
    onClick?.();
  }, [disabled, onClick, animType]);

  const animClass =
    animType === "bounce" ? "animate-btn-bounce" :
    animType === "shake"  ? "animate-shake" :
    "animate-btn-pop";

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        disabled={disabled}
        onAnimationEnd={() => setAnim(false)}
        className={`${className} ${anim ? animClass : ""} active:scale-95 transition-transform`}
      >
        {children}
      </button>
      {/* コイン飛び出しエフェクト */}
      {showCoin && (
        <div className="absolute top-0 left-1/2 -translate-x-1/2 pointer-events-none z-50">
          <span className="animate-coin-fly text-amber-400 text-lg font-black">
            <i className="ri-coin-line"></i>
          </span>
        </div>
      )}
    </div>
  );
}

export default function MapPage() {
  const { blocks, auctions, purchaseBlock } = useGame();
  const { user, addOwnedBlock, spendCoins, openAuthModal } = useAuth();
  const { t, lang } = useLang();
  const [selectedBlock, setSelectedBlock] = useState<SelectedBlock | null>(null);
  const [showBuild, setShowBuild] = useState(false);
  const [showAuction, setShowAuction] = useState(false);
  const [drawMode, setDrawMode] = useState(false);
  const [pendingPolygon, setPendingPolygon] = useState<{ polygon: LatLng[]; areaKm2: number } | null>(null);
  const [purchaseMsg, setPurchaseMsg] = useState<{ text: string; type: "success" | "error" } | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  const [flyTarget, setFlyTarget] = useState<{ lat: number; lng: number; zoom: number } | null>(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const myBlocks = user ? Object.values(blocks).filter((b) => b.ownerId === user.id) : [];
  const activeAuctions = auctions.slice(0, 5);

  const handleBlockSelect = useCallback((block: SelectedBlock | null) => {
    setSelectedBlock(block);
  }, []);

  const handleDrawComplete = useCallback((polygon: LatLng[], areaKm2: number) => {
    setDrawMode(false);
    setPendingPolygon({ polygon, areaKm2 });
    setPurchaseMsg(null);
  }, []);

  const handleDrawCancel = useCallback(() => {
    setDrawMode(false);
    setPendingPolygon(null);
  }, []);

  const handleFlyTo = useCallback((lat: number, lng: number, zoom: number) => {
    setFlyTarget({ lat, lng, zoom });
  }, []);

  // 面積表示ヘルパー（km² と m² のみ）
  const formatArea = (areaKm2: number): string => {
    const areaM2 = areaKm2 * 1_000_000;
    if (areaM2 < 1_000_000) {
      return `${areaM2.toFixed(0)} m²`;
    }
    return `${areaKm2.toFixed(4)} km²`;
  };

  const handleConfirmPurchase = async () => {
    if (!pendingPolygon) return;
    // 未ログインの場合はログインモーダルを表示
    if (!user) {
      openAuthModal("login");
      return;
    }
    const { polygon, areaKm2 } = pendingPolygon;
    const MIN_AREA = 0.0001;
    const MAX_AREA = 1;
    if (areaKm2 < MIN_AREA) {
      setPurchaseMsg({ text: lang === "ja" ? `面積が小さすぎます（${(areaKm2 * 1_000_000).toFixed(0)} m²）。100 m²以上必要です` : `Area too small. Need 100 m²+`, type: "error" });
      return;
    }
    if (areaKm2 >= MAX_AREA) {
      setPurchaseMsg({ text: lang === "ja" ? `面積が大きすぎます（${areaKm2.toFixed(4)} km²）。1 km²未満にしてください` : `Area too large. Must be under 1 km²`, type: "error" });
      return;
    }
    const centerLat = polygon.reduce((s, p) => s + p.lat, 0) / polygon.length;
    const centerLng = polygon.reduce((s, p) => s + p.lng, 0) / polygon.length;
    const blockId = `poly_${centerLat.toFixed(5)}_${centerLng.toFixed(5)}_${Date.now()}`;
    const totalPrice = calcLandPrice(areaKm2, centerLat, centerLng);

    setPurchasing(true);
    setPurchaseMsg(null);
    try {
      const result = await purchaseBlock(blockId, centerLat, centerLng, polygon, areaKm2, user.id, user.username, spendCoins);
      if (result.success) {
        await addOwnedBlock(blockId);
        setPurchaseSuccess(true);
        setShowConfetti(true);
        setPurchaseMsg({ text: lang === "ja" ? `購入完了！ -${totalPrice} coins` : `Purchase complete! -${totalPrice} coins`, type: "success" });
        setTimeout(() => setShowConfetti(false), 3000);
        setTimeout(() => {
          setPendingPolygon(null);
          setPurchaseMsg(null);
          setPurchaseSuccess(false);
        }, 2500);
      } else {
        setPurchaseMsg({ text: result.error || (lang === "ja" ? "購入できませんでした" : "Purchase failed"), type: "error" });
      }
    } catch {
      setPurchaseMsg({ text: lang === "ja" ? "エラーが発生しました" : "An error occurred", type: "error" });
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-[#0a0a0f] overflow-hidden">
      <Confetti active={showConfetti} count={150} duration={3000} />
      <Navbar />
      <div className="flex flex-1 overflow-hidden pt-16">
        {/* Left Sidebar */}
        <div className="hidden lg:flex flex-col w-72 bg-[#0d0d14] border-r border-white/5 overflow-y-auto">

          {/* 検索バー */}
          <div className="p-4 border-b border-white/5">
            <MapSearchBar onFlyTo={handleFlyTo} />
          </div>

          <div className="p-4 border-b border-white/5">
            {user ? (
              <div className="space-y-3 animate-slide-up-fade">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-amber-400/10 rounded-xl">
                    <i className="ri-user-3-line text-amber-400 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold whitespace-nowrap">{user.username}</p>
                    <p className="text-gray-500 text-xs whitespace-nowrap">{user.email}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-amber-400/10 rounded-xl p-3 text-center">
                    <p className="text-amber-400 text-lg font-black">{user.coins.toLocaleString()}</p>
                    <p className="text-gray-500 text-xs whitespace-nowrap">{t("coins")}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 text-center">
                    <p className="text-white text-lg font-black">{myBlocks.length}</p>
                    <p className="text-gray-500 text-xs whitespace-nowrap">{lang === "ja" ? "所有区画" : "Owned"}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white/5 rounded-xl p-4 text-center">
                <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-lock-line text-amber-400 text-xl"></i>
                </div>
                <p className="text-gray-400 text-xs">{t("loginToPlay")}</p>
              </div>
            )}
          </div>

          {/* Draw Button */}
          <div className="p-4 border-b border-white/5">
            {!drawMode ? (
              <AnimBtn
                onClick={() => { setPendingPolygon(null); setDrawMode(true); setSelectedBlock(null); }}
                animType="bounce"
                className="w-full bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-black font-bold rounded-xl py-3 text-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-edit-line text-base"></i>
                {lang === "ja" ? "土地を描いて購入" : "Draw & Buy Land"}
              </AnimBtn>
            ) : (
              <AnimBtn
                onClick={handleDrawCancel}
                animType="pop"
                className="w-full bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-400 font-bold rounded-xl py-3 text-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-close-line text-base"></i>
                {lang === "ja" ? "描画をキャンセル" : "Cancel Drawing"}
              </AnimBtn>
            )}

            {/* 描画ヒント */}
            {drawMode && (
              <div className="mt-3 bg-amber-400/5 border border-amber-400/20 rounded-xl p-3 space-y-1.5 animate-slide-up-fade">
                <p className="text-amber-400 text-xs font-semibold">{lang === "ja" ? "描画中..." : "Drawing..."}</p>
                <p className="text-gray-400 text-xs">{lang === "ja" ? "クリックで頂点を追加" : "Click to add vertices"}</p>
                <p className="text-gray-500 text-xs">{lang === "ja" ? "Shift: 直線スナップ" : "Shift: straight snap"}</p>
                <p className="text-gray-500 text-xs">{lang === "ja" ? "始点クリック or ダブルクリックで確定" : "Click start or dbl-click to finish"}</p>
                <p className="text-gray-500 text-xs">{lang === "ja" ? "ESC: キャンセル" : "ESC: cancel"}</p>
              </div>
            )}
          </div>

          {/* Pending Purchase Confirm */}
          {pendingPolygon && !drawMode && (
            <div className="p-4 border-b border-white/5 animate-slide-up-fade">
              <div className={`border rounded-xl p-4 space-y-3 transition-all ${purchaseSuccess ? "bg-emerald-400/10 border-emerald-400/30" : "bg-amber-400/10 border-amber-400/30"}`}>
                <p className={`text-xs font-bold ${purchaseSuccess ? "text-emerald-400" : "text-amber-400"}`}>
                  {purchaseSuccess
                    ? (lang === "ja" ? "🎉 購入完了！" : "🎉 Purchase Complete!")
                    : (lang === "ja" ? "購入確認" : "Confirm Purchase")}
                </p>
                {(() => {
                  const c = pendingPolygon.polygon;
                  const centerLat = c.reduce((s,p)=>s+p.lat,0)/c.length;
                  const centerLng = c.reduce((s,p)=>s+p.lng,0)/c.length;
                  const lv = getLandValueByLatLng(centerLat, centerLng);
                  const price = calcLandPrice(pendingPolygon.areaKm2, centerLat, centerLng);
                  const areaM2 = pendingPolygon.areaKm2 * 1_000_000;
                  const areaDisplay = formatArea(pendingPolygon.areaKm2);
                  const realPriceText = lv.avgLandPriceJpyM2
                    ? `約${((lv.avgLandPriceJpyM2 * areaM2) / 100000000).toFixed(0)}億円`
                    : lv.avgLandPriceUsdM2
                    ? `約$${((lv.avgLandPriceUsdM2 * areaM2) / 1000000).toFixed(1)}M`
                    : "";
                  return (
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-400">{lang === "ja" ? "面積" : "Area"}</span>
                        <span className="text-white font-bold">{areaDisplay}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-400">{lang === "ja" ? "地域" : "Region"}</span>
                        <span className="text-white text-xs">{lv.flag} {lv.country}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-400">{lang === "ja" ? "地価係数" : "Multiplier"}</span>
                        <span className="text-amber-300 font-bold">×{lv.landMultiplier}</span>
                      </div>
                      <div className="border-t border-white/10 pt-1.5">
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-400">{lang === "ja" ? "購入価格" : "Price"}</span>
                          <span className="text-amber-400 font-bold text-sm">{price.toLocaleString()} coins</span>
                        </div>
                      </div>
                      {/* 現実参考値（ゲーム内通貨とは別） */}
                      {(lv.avgLandPriceJpyM2 || lv.avgLandPriceUsdM2) && (
                        <div className="mt-2 bg-white/3 border border-white/8 rounded-lg px-2 py-1.5 space-y-1">
                          <p className="text-gray-600 text-xs font-medium">{lang === "ja" ? "── 現実参考値 ──" : "── Real World Ref ──"}</p>
                          {lv.avgLandPriceJpyM2 && (
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-600">{lang === "ja" ? "公示地価" : "Listed Price"}</span>
                              <span className="text-gray-500">{(lv.avgLandPriceJpyM2 / 10000).toFixed(0)}万円/m²</span>
                            </div>
                          )}
                          {lv.avgLandPriceUsdM2 && !lv.avgLandPriceJpyM2 && (
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-600">{lang === "ja" ? "地価" : "Land Price"}</span>
                              <span className="text-gray-500">${lv.avgLandPriceUsdM2.toLocaleString()}/m²</span>
                            </div>
                          )}
                          {realPriceText && (
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-600">{lang === "ja" ? "この面積の実勢" : "Est. Real Value"}</span>
                              <span className="text-gray-500">{realPriceText}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
                {pendingPolygon.areaKm2 < 0.0001 && (
                  <p className="text-red-400 text-xs animate-shake">{lang === "ja" ? "⚠ 100 m²以上必要です" : "⚠ Need at least 100 m²"}</p>
                )}
                {pendingPolygon.areaKm2 >= 1 && (
                  <p className="text-red-400 text-xs animate-shake">{lang === "ja" ? "⚠ 1 km²未満にしてください" : "⚠ Must be under 1 km²"}</p>
                )}
                {purchaseMsg && (
                  <div className={`rounded-lg px-2 py-1.5 text-xs animate-success-burst ${purchaseMsg.type === "success" ? "bg-emerald-400/10 text-emerald-400" : "bg-red-400/10 text-red-400"}`}>
                    {purchaseMsg.text}
                  </div>
                )}
                {!purchaseSuccess && (
                  <div className="space-y-2">
                    {!user && (
                      <div className="flex items-center gap-2 bg-amber-400/10 border border-amber-400/20 rounded-lg px-2.5 py-2">
                        <i className="ri-lock-line text-amber-400 text-xs flex-shrink-0"></i>
                        <p className="text-amber-400 text-xs whitespace-nowrap">
                          {lang === "ja" ? "購入にはログインが必要です" : "Login required to purchase"}
                        </p>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <AnimBtn
                        onClick={() => { setPendingPolygon(null); setPurchaseMsg(null); }}
                        animType="pop"
                        className="flex-1 py-2 rounded-lg bg-white/5 text-gray-400 text-xs cursor-pointer hover:bg-white/10 whitespace-nowrap"
                      >
                        {lang === "ja" ? "キャンセル" : "Cancel"}
                      </AnimBtn>
                      <AnimBtn
                        onClick={handleConfirmPurchase}
                        animType="bounce"
                        disabled={purchasing || pendingPolygon.areaKm2 < 0.0001 || pendingPolygon.areaKm2 >= 1}
                        className="flex-1 py-2 rounded-lg bg-gradient-to-r from-amber-400 to-orange-500 text-black font-bold text-xs cursor-pointer disabled:opacity-50 whitespace-nowrap flex items-center justify-center gap-1"
                      >
                        {purchasing ? (
                          <span className="flex items-center justify-center gap-1">
                            <span className="w-3 h-3 border-2 border-black/30 border-t-black rounded-full animate-spin"></span>
                          </span>
                        ) : !user ? (
                          <>
                            <i className="ri-login-box-line text-xs"></i>
                            <span>{lang === "ja" ? "ログインして購入" : "Login & Buy"}</span>
                          </>
                        ) : (lang === "ja" ? "購入する" : "Buy")}
                      </AnimBtn>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* My Blocks */}
          {myBlocks.length > 0 && (
            <div className="p-4 border-b border-white/5">
              <p className="text-gray-500 text-xs uppercase tracking-widest mb-3 font-medium whitespace-nowrap">{t("myBlocks")}</p>
              <div className="space-y-2">
                {myBlocks.map((b) => (
                  <div key={b.blockId} className="bg-white/5 hover:bg-white/8 rounded-xl p-3 cursor-pointer transition-all hover:translate-x-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white text-xs font-medium font-mono whitespace-nowrap">
                        {b.lat.toFixed(3)}, {b.lng.toFixed(3)}
                      </span>
                      {b.isForSale && (
                        <span className="text-amber-400 text-xs bg-amber-400/10 rounded-full px-2 py-0.5 whitespace-nowrap animate-glow-pulse">
                          {lang === "ja" ? "出品中" : "Auction"}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex gap-1">
                        {b.buildings.map((bld, i) => (
                          <span key={i} className="text-xs">
                            {bld.type === "house" ? "🏠" : bld.type === "company" ? "🏢" : "⚡"}
                          </span>
                        ))}
                        {b.buildings.length === 0 && <span className="text-gray-600 text-xs whitespace-nowrap">{lang === "ja" ? "建物なし" : "No buildings"}</span>}
                      </div>
                      <span className="text-gray-500 text-xs whitespace-nowrap">{formatArea(b.areaKm2 ?? 0)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active Auctions */}
          <div className="p-4">
            <p className="text-gray-500 text-xs uppercase tracking-widest mb-3 font-medium whitespace-nowrap">{t("activeAuctions")}</p>
            <div className="space-y-2">
              {activeAuctions.map((a) => (
                <div key={a.id} className="bg-white/5 rounded-xl p-3 hover:bg-white/8 transition-all hover:translate-x-1 cursor-pointer">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-white text-xs font-mono whitespace-nowrap">
                      {a.blockLat.toFixed(3)}, {a.blockLng.toFixed(3)}
                    </span>
                    <span className="text-amber-400 text-xs font-bold whitespace-nowrap">{a.currentBid.toLocaleString()} coins</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-xs whitespace-nowrap">{a.sellerName}</span>
                    <span className="text-gray-500 text-xs whitespace-nowrap">{a.duration}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Map Area */}
        <div className="flex-1 relative overflow-hidden">
          <WorldMap
            onBlockSelect={handleBlockSelect}
            drawMode={drawMode}
            onDrawComplete={handleDrawComplete}
            onDrawCancel={handleDrawCancel}
            flyTarget={flyTarget}
          />

          {selectedBlock && !drawMode && !pendingPolygon && (
            <BlockDetailModal
              block={selectedBlock}
              onClose={() => setSelectedBlock(null)}
              onOpenBuild={() => setShowBuild(true)}
              onOpenAuction={() => setShowAuction(true)}
            />
          )}
        </div>
      </div>

      {showBuild && selectedBlock && (
        <BuildModal blockId={selectedBlock.blockId} onClose={() => setShowBuild(false)} />
      )}
      {showAuction && selectedBlock && (
        <AuctionSetupModal blockId={selectedBlock.blockId} onClose={() => setShowAuction(false)} />
      )}
      <AuthModal />
    </div>
  );
}
